import { useRef, useEffect, useState, useCallback } from "react";
import VoiceMsgBubble from "./VoiceMsgBubble";
import QueryResultCard from "./query-results";
import type { Msg } from "../types";
import { useUserStore } from "@/store/useUserStore";
import { toastWarning } from "@/utils/toast";

/** 需要达人身份才能访问的路径前缀 */
const TALENT_REQUIRED_PREFIXES = ["/workspace"];

import type { TrainItem, CarItem, FoodItem, GuideItem, FlightItem, HotelSearchItem } from "@/api/aiChat";
import { unlockSharedAudio, playTtsViaWebAudio } from "@/utils/audioPlayer";
import { useVoiceStore } from "@/store/useVoiceStore";

/** TTS 音频播放按钮组件 */
function TtsPlayButton({ audioUrl, autoPlay = false }: { audioUrl: string; autoPlay?: boolean }) {
  const [playing, setPlaying] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  // ★ Web Audio API 播放器的清理函数引用
  const webAudioCleanupRef = useRef<(() => void) | null>(null);
  // ★ 用 ref 避免 handleToggle 捕获到过时的 playing/loading 状态
  const playingRef = useRef(false);
  const loadingRef = useRef(false);

  // ★ autoPlay 使用 Web Audio API 播放（AudioContext 解锁后不受手势限制）
  const loadAndPlayWebAudio = useCallback((url: string) => {
    console.log("[ChatBubble/TtsBtn] loadAndPlayWebAudio ★ autoPlay 触发, url=" + url.substring(0, 60) + "...");
    // 先清理上一次的播放
    webAudioCleanupRef.current?.();
    webAudioCleanupRef.current = null;

    loadingRef.current = true;
    playingRef.current = false;
    setLoading(true);
    setError(false);
    setPlaying(false);

    webAudioCleanupRef.current = playTtsViaWebAudio(url, {
      onLoadStart: () => { console.log("[ChatBubble/TtsBtn] onLoadStart"); },
      onCanPlay: () => {
        console.log("[ChatBubble/TtsBtn] onCanPlay → 音频解码完成");
        loadingRef.current = false;
        setLoading(false);
      },
      onPlay: () => {
        console.log("[ChatBubble/TtsBtn] onPlay → 开始播放 ★");
        playingRef.current = true;
        setPlaying(true);
        useVoiceStore.getState().setIsTtsPlaying(true);
      },
      onEnded: () => {
        console.log("[ChatBubble/TtsBtn] onEnded → 播放完毕");
        playingRef.current = false;
        setPlaying(false);
        useVoiceStore.getState().setIsTtsPlaying(false);
      },
      onPause: () => {
        console.log("[ChatBubble/TtsBtn] onPause");
        playingRef.current = false;
        setPlaying(false);
        useVoiceStore.getState().setIsTtsPlaying(false);
      },
      onError: () => {
        console.error("[ChatBubble/TtsBtn] onError → 播放失败!");
        loadingRef.current = false;
        setLoading(false);
        setError(true);
        useVoiceStore.getState().setIsTtsPlaying(false);
      },
    });
  }, []);

  // ★ 手动点击播放（复用 Web Audio API，自动走 AudioBuffer 缓存，秒播）
  const loadAndPlay = useCallback((url: string) => {
    console.log("[ChatBubble/TtsBtn] loadAndPlay ★ 手动点击播放, url=" + url.substring(0, 60) + "...");
    // 先清理可能正在进行的 Web Audio 播放
    webAudioCleanupRef.current?.();
    webAudioCleanupRef.current = null;

    loadingRef.current = true;
    playingRef.current = false;
    setLoading(true);
    setError(false);
    setPlaying(false);

    webAudioCleanupRef.current = playTtsViaWebAudio(url, {
      onLoadStart: () => { console.log("[ChatBubble/TtsBtn] loadAndPlay → onLoadStart"); },
      onCanPlay: () => {
        console.log("[ChatBubble/TtsBtn] loadAndPlay → onCanPlay");
        loadingRef.current = false;
        setLoading(false);
      },
      onPlay: () => {
        console.log("[ChatBubble/TtsBtn] loadAndPlay → onPlay ★");
        playingRef.current = true;
        setPlaying(true);
        useVoiceStore.getState().setIsTtsPlaying(true);
      },
      onEnded: () => {
        console.log("[ChatBubble/TtsBtn] loadAndPlay → onEnded");
        playingRef.current = false;
        setPlaying(false);
        useVoiceStore.getState().setIsTtsPlaying(false);
      },
      onError: () => {
        console.error("[ChatBubble/TtsBtn] loadAndPlay → onError ★ 播放失败!");
        loadingRef.current = false;
        setLoading(false);
        setError(true);
        useVoiceStore.getState().setIsTtsPlaying(false);
      },
    });
  }, []);

  // ★ 仅在 autoPlay=true 时自动播放（SSE 实时推送的 TTS）
  //    使用 Web Audio API，不依赖当前用户手势
  useEffect(() => {
    console.log("[ChatBubble/TtsBtn] ★★★★★ mount! autoPlay=" + autoPlay + ", audioUrl=" + (audioUrl ? audioUrl.substring(0, 60) + "..." : "undefined"));

    if (autoPlay && audioUrl) {
      console.log("[ChatBubble/TtsBtn] ★ autoPlay=true, 调用 loadAndPlayWebAudio ★");
      loadAndPlayWebAudio(audioUrl);
    } else if (!autoPlay) {
      console.log("[ChatBubble/TtsBtn] autoPlay=false, 不自动播放 (用户需手动点击播报)");
    } else if (!audioUrl) {
      console.log("[ChatBubble/TtsBtn] audioUrl 为空, 不自动播放");
    }
    // 仅首次挂载时检查 autoPlay
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 组件卸载时清理
  useEffect(() => {
    return () => {
      webAudioCleanupRef.current?.();
      webAudioCleanupRef.current = null;
      // ★ 确保卸载时重置 TTS 播放状态（cleanup 不会触发 onEnded）
      useVoiceStore.getState().setIsTtsPlaying(false);
    };
  }, []);

  const handleToggle = useCallback(() => {
    if (loadingRef.current) return;

    // ★ 利用用户点击手势解锁音频（兜底）
    unlockSharedAudio();

    // 如果正在播放 → 停止并重置状态（toggle off）
    if (playingRef.current) {
      webAudioCleanupRef.current?.();
      webAudioCleanupRef.current = null;
      playingRef.current = false;
      setPlaying(false);
      // ★ cleanup 不会触发 onEnded，需手动重置 TTS 播放状态
      useVoiceStore.getState().setIsTtsPlaying(false);
      return;
    }

    // ★ 开始播放（走 Web Audio API，自动命中 AudioBuffer 缓存秒播）
    loadAndPlay(audioUrl);
  }, [audioUrl, loadAndPlay]);

  return (
    <button
      onClick={handleToggle}
      className="flex items-center gap-1.5 px-3 py-1.5 rounded-full cursor-pointer active:scale-95 transition-all"
      style={{
        background: playing ? "#F0FDF4" : "#F5F6F8",
        border: `1px solid ${playing ? "#BBF7D0" : "#E5E7EB"}`,
      }}
    >
      {loading ? (
        <span className="inline-block w-3.5 h-3.5 border-2 border-t-transparent rounded-full animate-spin"
          style={{ borderColor: "#ccc", borderTopColor: "#999" }} />
      ) : error ? (
        <i className="ri-error-warning-line" style={{ color: "#EF4444", fontSize: 13 }} />
      ) : playing ? (
        <span className="flex items-end gap-[1.5px]" style={{ height: 14 }}>
          <style>{`@keyframes wvf{0%,100%{transform:scaleY(.3)}50%{transform:scaleY(1)}}`}</style>
          {[0, 0.12, 0.24, 0.12, 0].map((d, i) => (
            <span key={i} className="inline-block w-[2px] rounded-full"
              style={{ background: "#00C851", animation: `wvf 0.55s ease-in-out ${d}s infinite`, transformOrigin: "center", height: "100%" }} />
          ))}
        </span>
      ) : (
        <i className="ri-volume-up-line" style={{ color: "#666", fontSize: 13 }} />
      )}
      <span className="text-[11px] font-medium"
        style={{ color: playing ? "#00C851" : "#666" }}>
        {loading ? "加载中" : error ? "播放失败" : playing ? "正在播报" : "播报"}
      </span>
    </button>
  );
}

export default function ChatBubble({
  msg,
  onNav,
  onTrainSelect,
  onCarSelect,
  onFoodSelect,
  onGuideSelect,
  onFlightSelect,
  onTransferSelect,
  onHotelSelect,
}: {
  msg: Msg;
  onNav: (p: string) => void;
  onTrainSelect?: (train: TrainItem) => void;
  onCarSelect?: (car: CarItem) => void;
  onFoodSelect?: (food: FoodItem) => void;
  onGuideSelect?: (guide: GuideItem) => void;
  onFlightSelect?: (flight: FlightItem) => void;
  onTransferSelect?: (plan: any) => void;
  onHotelSelect?: (hotel: HotelSearchItem) => void;
}) {
  const isUser = msg.role === "user";
  const isTalent = useUserStore((s) => s.isTalent);

  // ★ 是否为带查询结果的 AI 消息（文字+卡片合体渲染）
  const hasQueryCard = !isUser && !msg.isTyping && !!msg.msg_type && !!msg.queryData;

  // ★ 同步检测 TTS url 是否为实时推送新增（非历史消息自带）
  //    历史消息加载时 url 已有值 → 首次渲染 prevUrlRef 不变 → autoPlay=false
  //    updateConversationMessage 新增 url → 下次渲染 prevUrlRef.current 仍为旧值 → autoPlay=true
  const prevUrlRef = useRef<string | undefined>(msg.url);
  const ttsAutoPlay = !!(msg.url && !prevUrlRef.current);
  // 渲染后同步 ref（不影响本次渲染，仅用于下次比较）
  useEffect(() => {
    prevUrlRef.current = msg.url;
  });

  const handleNav = (path: string) => {
    const needTalent = TALENT_REQUIRED_PREFIXES.some((prefix) =>
      path.startsWith(prefix),
    );
    if (needTalent && isTalent !== 1) {
      toastWarning("您还不是达人，无法进入工作台");
      return;
    }
    onNav(path);
  };

  // ★ 打字机流式输出：实时 AI 消息逐字显示，历史消息直接显示完整文本
  const [displayedText, setDisplayedText] = useState(msg._stream ? "" : msg.text);
  const [streamDone, setStreamDone] = useState(!msg._stream);

  useEffect(() => {
    if (!msg._stream) return;
    const chars = [...msg.text];
    if (chars.length === 0) { setStreamDone(true); return; }

    let i = 0;
    // 根据文本长度自适应速度：长文本稍快，保证体验一致
    const speed = Math.max(15, Math.min(35, Math.round(1200 / chars.length)));
    const timer = setInterval(() => {
      i++;
      if (i <= chars.length) {
        setDisplayedText(chars.slice(0, i).join(""));
      } else {
        clearInterval(timer);
        setStreamDone(true);
        // ★ 打字机动画结束，清除 _stream 标志（防止组件重挂载时二次播放）
        useVoiceStore.getState().updateConversationMessage(msg.id, { _stream: false });
      }
    }, speed);

    return () => clearInterval(timer);
  }, [msg.id, msg._stream, msg.text]);

  // ★ 光标闪烁组件
  const StreamCursor = !streamDone ? (
    <span
      className="inline-block w-0.5 ml-0.5 align-middle animate-pulse"
      style={{ background: "#00C851", borderRadius: 1, height: "1em" }}
    />
  ) : null;

  // ── 带查询卡片的 AI 消息：文字+卡片合在一个气泡内 ──
  if (hasQueryCard) {
    return (
      <div className="flex gap-2.5">
        <div
          className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
          style={{ background: "linear-gradient(135deg, #00C851, #00A043)" }}
        >
          <i className="ri-sparkling-2-fill text-white" style={{ fontSize: 15 }} />
        </div>
        <div className="flex flex-col gap-1.5 items-start flex-1 min-w-0">
          {/* 文字 + 卡片 合体容器 */}
          <div
            className="w-full"
            style={{
              background: "#fff",
              borderRadius: "4px 18px 18px 18px",
              border: "1px solid #EDEDED",
              boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
              overflow: "visible",
            }}
          >
            {/* AI 文字 */}
            <div
              className="px-4 pt-3 pb-2 text-sm leading-relaxed"
              style={{
                color: "#333",
                whiteSpace: "pre-line",
                lineHeight: "1.65",
              }}
            >
              {displayedText}{StreamCursor}
            </div>
            {/* 查询结果卡片（紧贴文字下方，与文字区域保持一致的内边距） */}
            <div className="px-4 pb-2.5">
              <QueryResultCard
                msg_type={msg.msg_type!}
                queryData={msg.queryData!}
                onTrainSelect={onTrainSelect}
                onCarSelect={onCarSelect}
                onFoodSelect={onFoodSelect}
                onGuideSelect={onGuideSelect}
                onFlightSelect={onFlightSelect}
                onTransferSelect={onTransferSelect}
                onHotelSelect={onHotelSelect}
              />
            </div>
          </div>

          {/* TTS 语音播报按钮（查询卡片场景） */}
          {msg.url && (
            <div className="mt-0.5">
              <TtsPlayButton audioUrl={msg.url} autoPlay={ttsAutoPlay} />
            </div>
          )}

          <span className="text-[10px]" style={{ color: "#bbb" }}>{msg.time}</span>
        </div>
      </div>
    );
  }

  return (
    <div className={`flex gap-2.5 ${isUser ? "flex-row-reverse" : ""}`}>
      {!isUser && (
        <div
          className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
          style={{ background: "linear-gradient(135deg, #00C851, #00A043)" }}
        >
          <i
            className="ri-sparkling-2-fill text-white"
            style={{ fontSize: 15 }}
          />
        </div>
      )}
      <div
        className={`flex flex-col gap-1.5 ${isUser ? "items-end" : "items-start"}`}
        style={{ maxWidth: "82%" }}
      >
        {/* 用户语音消息 */}
        {isUser && msg.isVoice && msg.url ? (
          <VoiceMsgBubble
            text={msg.text}
            duration={msg.duration ?? 0}
            url={msg.url}
          />
        ) : (
          <div
            className="px-4 py-3 text-sm leading-relaxed"
            style={{
              background: isUser
                ? "linear-gradient(135deg, #00C851, #00A043)"
                : "#fff",
              color: isUser ? "#fff" : "#333",
              borderRadius: isUser
                ? "18px 4px 18px 18px"
                : "4px 18px 18px 18px",
              whiteSpace: "pre-line",
              border: isUser ? "none" : "1px solid #EDEDED",
              lineHeight: "1.65",
              boxShadow: isUser ? "none" : "0 1px 3px rgba(0,0,0,0.04)",
            }}
          >
            {msg.isTyping ? (
              <div className="flex gap-1.5 items-center py-0.5">
                {[0, 1, 2].map((i) => (
                  <div
                    key={i}
                    className="w-1.5 h-1.5 rounded-full"
                    style={{
                      background: "#999",
                      animation: `bDot 1s ease-in-out ${i * 0.2}s infinite`,
                    }}
                  />
                ))}
              </div>
            ) : (
              <>{displayedText}{StreamCursor}</>
            )}
          </div>
        )}

        {/* 跳转按钮 */}
        {!isUser && !msg.isTyping && msg.action && (
          <button
            onClick={() => handleNav(msg.action!.path)}
            className="flex items-center gap-2 px-4 py-2.5 rounded-2xl text-xs font-semibold cursor-pointer active:scale-95 transition-all whitespace-nowrap mt-0.5"
            style={{
              background: "linear-gradient(135deg, #00C851, #00A043)",
              color: "#fff",
              letterSpacing: "0.01em",
            }}
          >
            <i className={msg.action.icon} style={{ fontSize: 13 }} />
            {msg.action.label}
            <i
              className="ri-arrow-right-line"
              style={{ fontSize: 11, opacity: 0.7 }}
            />
          </button>
        )}

        {/* TTS 语音播报按钮 */}
        {!isUser && !msg.isTyping && msg.url && !msg.isVoice && (
          <div className="mt-1">
            <TtsPlayButton audioUrl={msg.url} autoPlay={ttsAutoPlay} />
          </div>
        )}

        <span
          className="text-[10px]"
          style={{ color: "#bbb" }}
        >
          {msg.time}
        </span>
      </div>
    </div>
  );
}
